<template>
  <div
    class="star-rating"
    :class="{ 'star-rating--disabled': disabled }"
    role="radiogroup"
    :aria-label="disabled ? `Rating: ${modelValue ?? 0} out of 5 stars` : 'Song rating'"
  >
    <!-- Zero star icon (crossed-out circle) -->
    <button
      type="button"
      role="radio"
      class="star-rating__zero"
      :class="{
        'star-rating__zero--active': modelValue === 0,
        'star-rating__zero--hovered': hoverRating === -1,
      }"
      :disabled="disabled"
      :aria-checked="modelValue === 0"
      aria-label="Rate zero stars"
      @click="handleZeroClick"
      @mouseenter="handleMouseEnter(-1)"
      @mouseleave="handleMouseLeave"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
        <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
    </button>
    <button
      v-for="star in 5"
      :key="star"
      type="button"
      role="radio"
      class="star-rating__star"
      :class="{
        'star-rating__star--filled': star <= displayRating,
        'star-rating__star--hovered': star <= hoverRating && hoverRating > 0,
      }"
      :disabled="disabled"
      :aria-checked="modelValue === star"
      :aria-label="`Rate ${star} star${star > 1 ? 's' : ''}`"
      @click="handleClick(star)"
      @mouseenter="handleMouseEnter(star)"
      @mouseleave="handleMouseLeave"
    >
      <v-icon size="20">
        {{ star <= displayRating ? "mdi-star" : "mdi-star-outline" }}
      </v-icon>
    </button>
    <span v-if="showCount && totalRatings > 0" class="star-rating__count">
      ({{ totalRatings }})
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: number | null;
    disabled?: boolean;
    showCount?: boolean;
    totalRatings?: number;
  }>(),
  {
    modelValue: null,
    disabled: false,
    showCount: false,
    totalRatings: 0,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: number | null): void;
  (e: "rate", value: number): void;
  (e: "remove"): void;
  (e: "zero"): void;
}>();

const hoverRating = ref(0);

const displayRating = computed(() => {
  if (hoverRating.value > 0) return hoverRating.value;
  return props.modelValue ?? 0;
});

function handleClick(star: number) {
  if (props.disabled) return;

  // If clicking the same rating, remove it
  if (star === props.modelValue) {
    emit("update:modelValue", null);
    emit("remove");
  } else {
    emit("update:modelValue", star);
    emit("rate", star);
  }
}

function handleZeroClick() {
  if (props.disabled) return;

  // If already zero, remove it
  if (props.modelValue === 0) {
    emit("update:modelValue", null);
    emit("remove");
  } else {
    emit("update:modelValue", 0);
    emit("zero");
    emit("rate", 0);
  }
}

function handleMouseEnter(star: number) {
  if (!props.disabled) {
    hoverRating.value = star;
  }
}

function handleMouseLeave() {
  hoverRating.value = 0;
}
</script>

<style scoped lang="scss">
.star-rating {
  display: inline-flex;
  align-items: center;
  gap: 2px;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__zero {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: #78716c;
    transition:
      color 0.15s ease,
      transform 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 4px;

    &:hover:not(:disabled) {
      transform: scale(1.1);
      color: #ef4444;
    }

    &--active,
    &--hovered {
      color: #ef4444;
    }

    &:disabled {
      cursor: default;
    }
  }

  &__star {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: #78716c;
    transition:
      color 0.15s ease,
      transform 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      transform: scale(1.1);
    }

    &--filled,
    &--hovered {
      color: #f59e0b;
    }

    &:disabled {
      cursor: default;
    }
  }

  &__count {
    font-size: 0.75rem;
    color: #78716c;
    margin-left: 4px;
  }
}

// Light theme
.v-theme--light {
  .star-rating {
    &__zero {
      color: #a8a29e;

      &:hover:not(:disabled) {
        color: #dc2626;
      }

      &--active,
      &--hovered {
        color: #dc2626;
      }
    }

    &__star {
      color: #a8a29e;

      &--filled,
      &--hovered {
        color: #f59e0b;
      }
    }

    &__count {
      color: #78716c;
    }
  }
}
</style>
